function getNestedValue<T>(obj: T, path: string): string {
  // @ts-ignore
  return path.split('.').reduce((val: T, key: string) => (val && val[key] !== undefined ? val[key] : ''), obj) as string
}

function getNaturalSortKey(str: string): (string | number)[] {
  const chunks = str.split(/(\d+)/).filter(Boolean)

  return chunks.map(chunk => {
    if (/^\d+$/.test(chunk)) {
      return parseInt(chunk, 10)
    }
    return chunk.toLowerCase().trim()
  })
}

function compareNaturalSortKeys(a: (string | number)[], b: (string | number)[]): number {
  const minLength = Math.min(a.length, b.length)

  for (let i = 0; i < minLength; i++) {
    const valueA = a[i]
    const valueB = b[i]

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      if (valueA !== valueB) {
        return valueA - valueB
      }
    }
    else if (typeof valueA === 'string' && typeof valueB === 'string') {
      // Code-point order, not localeCompare. getNaturalSortKey has already lowercased these, so
      // for ASCII the two agree; localeCompare would additionally vary with the runtime's locale
      // and could never be matched by the backend, which sorts the same lists for the exports.
      if (valueA < valueB) {
        return -1
      }
      if (valueA > valueB) {
        return 1
      }
    }
    else if (typeof valueA === 'number' && typeof valueB === 'string') {
      return -1
    } else if (typeof valueA === 'string' && typeof valueB === 'number') {
      return 1
    }
  }

  return a.length - b.length
}


/**
 * Compare two human-readable keys the way a person reads them.
 *
 * Splits each string into digit runs and text runs, then compares run by run: numbers
 * numerically, text in case-insensitive code-point order, and a number before text when the two
 * disagree in type. Deliberately locale-free so the backend can produce the same order.
 * A shorter key that is otherwise identical sorts first.
 *
 * This is what `sortTasks` has always used; it is exported so callers that need the ORDER without
 * the sorting — a comparator inside a larger comparison, say — do not reimplement it. Position
 * keys had a second implementation that split on "." and ran `Number()` over the parts, which
 * turned every letter into NaN: "CBUS" collapsed to 0 and sorted above everything, and "1.1a"
 * landed before "1.1".
 *
 * @param a - first key, e.g. "1.10"
 * @param b - second key, e.g. "1.2"
 * @returns negative when a sorts first, positive when b does, 0 when they tie
 * @example
 * compareNaturalKeys('1.2', '1.10')   // negative — 2 < 10, not "1.2" > "1.10"
 * compareNaturalKeys('1.1a', '1.1')   // positive — the longer key sorts after
 * compareNaturalKeys('CBUS', '1.1')   // positive — text sorts after numbers
 */
export function compareNaturalKeys(a: string, b: string): number {
  return compareNaturalSortKeys(getNaturalSortKey(a), getNaturalSortKey(b))
}

/**
 * Order two rows by their position key, then by their name, with keyless rows last.
 *
 * This is the app's one rule for "how a keyed list is ordered", shared by the Study Budgets
 * positions table and the project module's task tables so the same list cannot be ordered two
 * different ways on two screens.
 *
 * Three parts, in order:
 *   1. A row WITH a key sorts before a row without one.
 *   2. Two keyed rows compare by key, naturally — so "1.2" precedes "1.10".
 *   3. Ties break on the name.
 *
 * Ranking keyless rows by rank rather than by name is what makes this transitive. Comparing by
 * name whenever either side lacked a key made the rule depend on the pair: 1.1 "Zulu" beat
 * 2.1 "Alpha" by key, "Mike" (no key) beat "Zulu" by name, "Alpha" beat "Mike" by name — so
 * Mike < Zulu < Alpha < Mike, and Array.prototype.sort may return anything at all for a
 * comparator like that. Ordering on the tuple (has key, key, name) cannot produce a cycle.
 *
 * @param aKey - first row's key; empty string, null or undefined all mean "no key"
 * @param aName - first row's name
 * @param bKey - second row's key
 * @param bName - second row's name
 * @returns negative when a sorts first, positive when b does, 0 when the two tie
 * @example
 * compareKeyThenName('1.2', 'Zulu', '1.10', 'Alpha')   // negative — 2 < 10, name ignored
 * compareKeyThenName('', 'Alpha', '1.1', 'Zulu')       // positive — keyless sorts last
 * compareKeyThenName('1.1', 'Beta', '1.1', 'Alpha')    // positive — same key, name decides
 */
export function compareKeyThenName(
  aKey: string | null | undefined,
  aName: string | null | undefined,
  bKey: string | null | undefined,
  bName: string | null | undefined
): number {
  const keyA = aKey || ''
  const keyB = bKey || ''

  if ((keyA !== '') !== (keyB !== '')) {
    return keyA !== '' ? -1 : 1
  }
  if (keyA !== '') {
    const byKey = compareNaturalKeys(keyA, keyB)
    if (byKey !== 0) {
      return byKey
    }
  }
  // Case-insensitive code-point order, deliberately NOT localeCompare.
  //
  // localeCompare with no locale uses the runtime's, so the same list came out in different
  // orders for different users: with German names, sv-SE puts "Ärztliche Betreuung" last while
  // de-DE and en-US put it first. It also cannot be matched by the backend, which has no ICU —
  // and the exports have to produce the same order as the screen, or the two disagree the way
  // they did before this rule existed.
  //
  // Lowercasing first is what keeps "alpha" before "Beta"; raw code points would put every
  // capital ahead of every lowercase letter. Mirrored exactly in
  // trialytix/helpers/task_ordering.py.
  //
  // Accented characters sort after z rather than beside their base letter. 2 of 825 live task
  // names are affected, and agreeing with the export is worth more than that ordering.
  const nameA = (aName || '').toLowerCase()
  const nameB = (bName || '').toLowerCase()
  if (nameA < nameB) return -1
  if (nameA > nameB) return 1
  return 0
}

/**
 * Sort tasks by their key then their name, keyless last.
 *
 * The key and the name are shown merged in the UI ("1.11 Conference Calls") but stored in two
 * separate fields, so both paths are given explicitly rather than derived from one another.
 *
 * Returns a new array — unlike `sortTasks`, which sorts in place. Several callers pass arrays
 * built inside selectors, where sorting in place mutates derived state.
 *
 * @param tasks - rows to sort
 * @param keyPath - dotted path to the key, e.g. "key" or "offer_direct_cost.key"
 * @param namePath - dotted path to the name, e.g. "offer_position"
 * @returns a new, sorted array
 * @example
 * sortTasksByKey(costs, 'key', 'offer_position')
 * sortTasksByKey(costs, 'offer_direct_cost.key', 'offer_direct_cost.offer_position')
 */
export function sortTasksByKey<T>(tasks: T[], keyPath: string, namePath: string): T[] {
  return [...tasks].sort((a, b) =>
    compareKeyThenName(
      String(getNestedValue(a, keyPath) || ''),
      String(getNestedValue(a, namePath) || ''),
      String(getNestedValue(b, keyPath) || ''),
      String(getNestedValue(b, namePath) || '')
    )
  )
}

export function sortTasks<T>(tasks: T[], keyPath: string): T[] {
  return tasks.sort((a, b) => {
    const valueA = String(getNestedValue(a, keyPath) || '')
    const valueB = String(getNestedValue(b, keyPath) || '')

    const keysA = getNaturalSortKey(valueA)
    const keysB = getNaturalSortKey(valueB)

    return compareNaturalSortKeys(keysA, keysB)
  })
}
